import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Logger, LogLevel } from '@utils/logger';
import { environment } from '../../environments/environment';
import prodEnvConfig from '../../assets/data/config.ENVPROD.json';
import { WINDOW } from '@ng-web-apis/common';
import { LiteratureApimFlows } from './app-state.service';

export interface EnvConfig {
  apimFlags?: LiteratureApimFlows;
  accountsApiUrl?: string;
  canadaAccountsApiUrl?: string;
  canadaSignOutApiURL?: string;
  myfundsApiUrl?: string;
  preMyfundsApiUrl?: string;
  accountsDomain?: string;
  myfundsDomain?: string;
  preMyfundsDomain?: string;
  apiPort?: number;
  apiScheme?: string;
  apiUrl?: string;
  auth?: string;
  bookApptBaseUrl?: string;
  cmsBaseUrl?: string;
  commentaryContentUrl?: string;
  cookieApiUrl?: string;
  defaultLogLevel?: LogLevel;
  defaultPds?: string;
  environment?: string;
  searchEnvironment?: string;
  fundCommentaryKey?: string;
  hypoBaseUrl?: string;
  hypoSavedPresentationsUrl?: string;
  insightsUrl?: string;
  autoCompleteUrl?: string;
  instrumentationKey?: string;
  literatureApiUrl?: string;
  literatureBaseApiUrl?: string;
  literatureDownloadBaseUrl?: string;
  literatureDownloadFinraUrl?: string;
  literatureDownloadUrl?: string;
  literatureDownloadZipUrl?: string;
  literatureImageUrl?: string;
  literatureOrderHistoryList?: string;
  literatureRecentOrderHistoryList?: string;
  profileDomain?: string;
  searchStatusUrl?: string;
  searchUrl?: string;
  singleHost?: boolean;
  spabaseurl?: string;
  ssoUrl?: string;
  ssoPing?: string;
  widenUrl?: string;
  widenProxyUrl?: string;
  resourceApiTimeoutMs?: number;
  errorPage?: string;
  salesTeamUrl?: string;
  useLiteratureAPIMFlow?: boolean;
  orderHistoryAPIMUrl?: string;
  orderHistoryDetailAPIMUrl?: string;
  recentOrderAPIMUrl?: string;
  submitOrderAPIMUrl?: string;
  literatureAuthAPIAPIMKey?: string;
  marketingAuthAPIMKey?: string;
  literatureAPIMKey?: string;
  salesTeamListUrl?: string;
  dynamicsClientId?: string;
  dynamicsClientSecret?: string;
  LiteratureBaseAPIMUrl?: string;
  LiteratureEmailDocsAPIMUrl?: string;
  LiteratureDownloadZipAPIMUrl?: string;
  myfundsApiBase?: string;
  myfundsApiKey?: string;
  germanCalculatorOnetimeInvestmentApiUrl?: string;
  germanCalculatorSavingPlanApiUrl?: string;
  germanCalculatorWithdrawlPlanApiUrl?: string;
  germanCalculatorPensionApiUrl?: string;
  germanCalculatorPensionEstimatorApiUrl?: string;
  shortfallClosureApiUrl?: string;
  toGetFavoriteFundApiUrl?: string;
  toSetFavoriteFundApiUrl?: string;
  favoriteFundApimHeaderKey?: string;
  useLocalStorageFavoriteFunds?: boolean;
  calcFtApiKey?: string;
  ocpApimTrace?: boolean;
  datadogApplicationId?: string;
  datadogService?: string;
  datadogSite?: string;
  datadogToken?: string;
  datadogAllowedOrigins?: (string | RegExp)[];
  useCommentaryContentFromTS?: boolean;
  literatureDownloadAutoNumberingUrl?: string;
  useLiteratureOverrideListFor?: string[];
}

/**
 * Environmental Config loaded from config.json
 */
@Injectable({
  providedIn: 'root',
})
export class EnvConfigService {
  private configName: string = environment.config;
  private envConfig: EnvConfig;
  private readonly isProd: boolean;
  private readonly isPreEnv: boolean;
  private loadedPromise: Promise<boolean>;

  constructor(
    private http: HttpClient,
    @Inject(WINDOW) readonly windowRef: Window
  ) {
    const hostname = windowRef.location.hostname;
    this.isPreEnv = hostname.startsWith('pre');
    this.isProd = hostname.startsWith('www') || this.isPreEnv;
    if (this.isProd) {
      this.initialize(prodEnvConfig);
      this.loadedPromise = Promise.resolve(true);
    }
  }

  public loadEnvConfig(): Promise<boolean> {
    if (!this.loadedPromise) {
      this.loadedPromise = new Promise((resolve) => {
        this.http
          .get<EnvConfig>('/assets/data/' + this.configName)
          .subscribe((data) => {
            this.initialize(data);
            resolve(true);
          });
      });
    }
    return this.loadedPromise;
  }

  private initialize(data: EnvConfig) {
    this.envConfig = data;
    Logger.addConfiguration({
      defaultLevel: this.envConfig.defaultLogLevel || LogLevel.DEBUG,
    });
  }
  public getEnvConfig(): EnvConfig {
    return this.envConfig;
  }

  public isProduction(): boolean {
    return this.isProd;
  }

  public isPre(): boolean {
    return this.isPreEnv;
  }
}
